import React, { FC, useEffect, useState } from 'react';
import { Form, InputNumber, Select } from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import styled from 'styled-components';

import { newCreationFlowEvents } from '../../../common/Scribe/EventsList';
import { Constraint, Operators } from '../../../lib/App';
import { useTreatments } from '../contexts/ExperimentFormInfoProvider';
import {
  ExperimentAssignmentStrategy,
  ExpFormField,
  TimeSliceTimeUnit,
} from '../helper/consts';
import ColorIndicator from './ColorIndicator';
import GroupSelector from './GroupSelector';
import { getAllDomains } from '@/common/domains';
import { detectValueItemType } from '@/common/valueItemType';

interface TreatmentAssignmentInputProps {
  color?: string;
  treatmentName: string;
  treatmentId: string;
  strategy?: string;
  disabled?: boolean;
  defaultValue?: number;
}

export const TreatmentAssignmentInput: FC<TreatmentAssignmentInputProps> = ({
  color = '#E8E8E8',
  treatmentName,
  treatmentId,
  strategy,
  defaultValue,
  disabled,
}) => {
  const form = Form.useFormInstance();
  const [treatments] = useTreatments();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const currentStrategyTreatmentValue = Form.useWatch(
    ExpFormField.STRATEGY.TREATMENT_VALUE(treatmentId),
    { form, preserve: true },
  );

  const currentTimeType = Form.useWatch(ExpFormField.STRATEGY.TIME_TYPE, {
    form,
    preserve: true,
  });
  const currentBufferType = Form.useWatch(ExpFormField.STRATEGY.BUFFER_TYPE, {
    form,
    preserve: true,
  });
  const currentDomainName = Form.useWatch(ExpFormField.DOMAIN, {
    form,
    preserve: true,
  });

  const currentExplicitGroup = Form.useWatch(
    ExpFormField.STRATEGY.GROUP(treatmentId),
    {
      form,
      preserve: true,
    },
  );

  const currentStrategyTreatmentName = Form.useWatch(
    ExpFormField.STRATEGY.NAME,
    {
      form,
      preserve: true,
    },
  );
  const currentStrategyTreatmentAllValues = Form.useWatch(
    ExpFormField.STRATEGY.ALL_VALUES,
    { form, preserve: true },
  );

  const [constraint, setConstraint] = useState<Constraint>({
    op: Operators['='],
    valueItemType: '',
    value: currentExplicitGroup,
  });

  useEffect(() => {
    setConstraint({
      ...constraint,
      target: getAllDomains().find(
        (domain) => domain.name === currentDomainName,
      )?.targetBy,
    });
  }, [currentDomainName]);

  useEffect(() => {
    if (currentExplicitGroup !== constraint.value) {
      const constraintCopy = {
        ...constraint,
        value: currentExplicitGroup,
      };

      constraintCopy.target = currentExplicitGroup
        ? currentExplicitGroup.substring(0, currentExplicitGroup.indexOf('.'))
        : '';
      constraintCopy.valueItemType = detectValueItemType(constraintCopy);

      setConstraint(constraintCopy);
    }
  }, [currentExplicitGroup]);

  useEffect(() => {
    if (
      currentStrategyTreatmentName ===
        ExperimentAssignmentStrategy.WEIGHTED_CHOICE &&
      currentStrategyTreatmentAllValues
    ) {
      const existingTreatmentValues = Object.entries(
        currentStrategyTreatmentAllValues,
      )
        .filter(([key]) => treatments.find((t) => t.id === key))
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(([_, val]) => val);
      if (existingTreatmentValues.includes('')) {
        setErrorMessage('Weight cannot be empty');
        return;
      }
      if (existingTreatmentValues.includes('0')) {
        setErrorMessage('Weight cannot be zero');
        return;
      }
      const totalWeight = existingTreatmentValues.reduce(
        (acc: number, val: number) => Number(acc) + Number(val),
        0,
      );
      if (totalWeight !== 100) {
        setErrorMessage('Total weight must be 100%');
      } else {
        setErrorMessage('');
      }
    } else {
      setErrorMessage('');
    }
  }, [
    treatments.length,
    currentStrategyTreatmentName,
    currentStrategyTreatmentAllValues,
  ]);

  const renderInput = (strategy: string) => {
    if (
      [
        ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
        ExperimentAssignmentStrategy.UNIFORM_CHOICE,
      ].includes(strategy)
    ) {
      return (
        <Form.Item
          name={ExpFormField.STRATEGY.TREATMENT_VALUE(treatmentId)}
          style={{ margin: 0 }}
          validateStatus={errorMessage ? 'error' : ''}
          help={errorMessage}
          rules={[
            {
              validator: () => {
                if (errorMessage) {
                  return Promise.reject(errorMessage);
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <WeightedInputContainer color={color}>
            <InputNumber
              suffix="%"
              controls={false}
              value={
                currentStrategyTreatmentValue
                  ? currentStrategyTreatmentValue
                  : defaultValue
              }
              type="number"
              precision={0}
              max={100}
              min={0}
              bordered={false}
              style={{
                width: '100%',
                border: 'none',
                backgroundColor: disabled ? 'rgba(0, 0, 0, 0.04)' : '',
              }}
              onChange={(value) => {
                if (
                  String(value) !== currentStrategyTreatmentValue &&
                  currentStrategyTreatmentName ===
                    ExperimentAssignmentStrategy.UNIFORM_CHOICE
                ) {
                  form.setFieldValue(
                    ExpFormField.STRATEGY.TREATMENT_VALUE(treatmentId),
                    value,
                  );
                  form.setFieldValue(
                    ExpFormField.STRATEGY.NAME,
                    ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
                  );
                }
              }}
            />
          </WeightedInputContainer>
        </Form.Item>
      );
    }
    if (
      [
        ExperimentAssignmentStrategy.TIME_SLICE,
        ExperimentAssignmentStrategy.TIME_SLICE_V2,
        ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
        ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE,
      ].includes(strategy)
    ) {
      return (
        <TimeSliceInputContainer color={color}>
          <div className="configTitle">Time</div>

          <TimeConfigGroup>
            <Form.Item
              name={ExpFormField.STRATEGY.TIME_VALUE(treatmentId)}
              style={{ margin: 0, flex: 1, display: 'block' }}
              rules={[{ required: true, message: 'Time span is required' }]}
            >
              <InputNumber
                disabled={disabled}
                type="number"
                style={{ width: '100%' }}
                min={0}
                onChange={(value) => {
                  scribe.sendEvent(
                    newCreationFlowEvents.changeTimesliceValue.name,
                    {
                      experiment_name: form.getFieldValue(ExpFormField.NAME),
                      assignment_strategy: strategy,
                      treatment_id: treatmentId,
                      time: value,
                      time_unit:
                        form.getFieldValue(ExpFormField.STRATEGY.TIME_TYPE) ||
                        TimeSliceTimeUnit.SECONDS,
                      buffer_time:
                        form.getFieldValue(
                          ExpFormField.STRATEGY.BUFFER_VALUE(treatmentId),
                        ) || 0,
                      buffer_time_unit:
                        form.getFieldValue(ExpFormField.STRATEGY.BUFFER_TYPE) ||
                        TimeSliceTimeUnit.SECONDS,
                      STATE_NAME:
                        newCreationFlowEvents.changeTimesliceValue.state,
                    },
                  );
                  form.setFieldValue(
                    ExpFormField.STRATEGY.TIME_VALUE(treatmentId),
                    value,
                  );
                }}
              />
            </Form.Item>
            <Select
              disabled={disabled}
              className="timeSliceInput"
              defaultValue={TimeSliceTimeUnit.SECONDS}
              value={currentTimeType}
              onChange={(value) => {
                scribe.sendEvent(
                  newCreationFlowEvents.changeTimesliceUnit.name,
                  {
                    experiment_name: form.getFieldValue(ExpFormField.NAME),
                    assignment_strategy: strategy,
                    treatment_id: treatmentId,
                    time:
                      form.getFieldValue(
                        ExpFormField.STRATEGY.TIME_VALUE(treatmentId),
                      ) || 0,
                    time_unit: value,
                    buffer_time:
                      form.getFieldValue(
                        ExpFormField.STRATEGY.BUFFER_VALUE(treatmentId),
                      ) || 0,
                    buffer_time_unit:
                      form.getFieldValue(ExpFormField.STRATEGY.BUFFER_TYPE) ||
                      TimeSliceTimeUnit.SECONDS,
                    STATE_NAME: newCreationFlowEvents.changeTimesliceUnit.state,
                  },
                );

                form.setFieldValue(ExpFormField.STRATEGY.TIME_TYPE, value);
              }}
              options={[
                { label: 'seconds', value: TimeSliceTimeUnit.SECONDS },
                { label: 'minutes', value: TimeSliceTimeUnit.MINUTES },
                { label: 'hours', value: TimeSliceTimeUnit.HOURS },
              ]}
            />
          </TimeConfigGroup>

          <div className="configTitle">Buffer Time</div>

          <TimeConfigGroup>
            <Form.Item
              name={ExpFormField.STRATEGY.BUFFER_VALUE(treatmentId)}
              style={{ margin: 0, flex: 1, display: 'block' }}
              rules={[{ required: true, message: 'Buffer span is required' }]}
            >
              <InputNumber
                disabled={disabled}
                type="number"
                min={0}
                style={{ width: '100%' }}
                onChange={(value) => {
                  scribe.sendEvent(
                    newCreationFlowEvents.changeTimesliceValue.name,
                    {
                      experiment_name: form.getFieldValue(ExpFormField.NAME),
                      assignment_strategy: strategy,
                      treatment_id: treatmentId,
                      time:
                        form.getFieldValue(
                          ExpFormField.STRATEGY.TIME_VALUE(treatmentId),
                        ) || 0,
                      time_unit:
                        form.getFieldValue(ExpFormField.STRATEGY.TIME_TYPE) ||
                        TimeSliceTimeUnit.SECONDS,
                      buffer_time: value,
                      buffer_time_unit:
                        form.getFieldValue(ExpFormField.STRATEGY.BUFFER_TYPE) ||
                        TimeSliceTimeUnit.SECONDS,
                      STATE_NAME:
                        newCreationFlowEvents.changeTimesliceValue.state,
                    },
                  );
                  form.setFieldValue(
                    ExpFormField.STRATEGY.BUFFER_VALUE(treatmentId),
                    value,
                  );
                }}
              />
            </Form.Item>
            <Select
              disabled={disabled}
              className="timeSliceInput"
              defaultValue={TimeSliceTimeUnit.SECONDS}
              value={currentBufferType}
              onChange={(value) => {
                scribe.sendEvent(
                  newCreationFlowEvents.changeTimesliceUnit.name,
                  {
                    experiment_name: form.getFieldValue(ExpFormField.NAME),
                    assignment_strategy: strategy,
                    treatment_id: treatmentId,
                    time:
                      form.getFieldValue(
                        ExpFormField.STRATEGY.TIME_VALUE(treatmentId),
                      ) || 0,
                    time_unit:
                      form.getFieldValue(ExpFormField.STRATEGY.TIME_TYPE) ||
                      TimeSliceTimeUnit.SECONDS,
                    buffer_time:
                      form.getFieldValue(
                        ExpFormField.STRATEGY.BUFFER_VALUE(treatmentId),
                      ) || 0,
                    buffer_time_unit: value,
                    STATE_NAME: newCreationFlowEvents.changeTimesliceUnit.state,
                  },
                );
                form.setFieldValue(ExpFormField.STRATEGY.BUFFER_TYPE, value);
              }}
              options={[
                { label: 'seconds', value: TimeSliceTimeUnit.SECONDS },
                { label: 'minutes', value: TimeSliceTimeUnit.MINUTES },
                { label: 'hours', value: TimeSliceTimeUnit.HOURS },
              ]}
            />
          </TimeConfigGroup>
        </TimeSliceInputContainer>
      );
    }
    if (strategy === ExperimentAssignmentStrategy.EXPLICIT_GROUP) {
      return (
        <ExplicitInputContainer>
          <GroupSelector
            disableInlineGroup
            mode={null}
            constraint={constraint}
            onChange={(constraint: Constraint) => {
              form.setFieldValue(
                ExpFormField.STRATEGY.GROUP(treatmentId),
                constraint.value,
              );

              setConstraint(constraint);
            }}
            disabled={disabled}
          />
        </ExplicitInputContainer>
      );
    }
  };
  return (
    <Wrapper>
      <Header>
        <ColorIndicator color={color} />
        <div>{treatmentName}</div>
      </Header>
      {renderInput(strategy)}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  min-width: 350px;
  max-width: 500px;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const BaseInputContainer = styled.div`
  border-radius: 14px;
  font-size: 14px;
  border: ${(props) => `1px solid ${props.color}`};
`;

const WeightedInputContainer = styled(BaseInputContainer)`
  width: 100%;
`;

const TimeSliceInputContainer = styled(BaseInputContainer)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border: ${(props) => `1px solid ${props.color}`};
  & .configTitle {
    font-size: 12px;
  }
`;

const TimeConfigGroup = styled.div`
  align-items: start;
  display: flex;
  gap: 8px;
  & .timeSliceInput {
    flex: 1;
  }
`;

const ExplicitInputContainer = styled(BaseInputContainer)`
  width: 100%;
`;

export default TreatmentAssignmentInput;
